/**
 * Armazenamento do JWT fora de localStorage (sessionStorage).
 * Reduz persistência do token após fechar o browser e evita o vetor
 * clássico de XSS lendo localStorage.token.
 *
 * Migra automaticamente tokens legados em localStorage na primeira leitura.
 */

const TOKEN_KEY = 'token';
const AUTH_PROVIDER_KEY = 'auth_provider';

function canUseSessionStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch {
    return false;
  }
}

function canUseLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

export function getAuthToken(): string | null {
  if (!canUseSessionStorage()) return null;
  const fromSession = sessionStorage.getItem(TOKEN_KEY);
  if (fromSession) return fromSession;

  if (canUseLocalStorage()) {
    const legacy = localStorage.getItem(TOKEN_KEY);
    if (legacy) {
      sessionStorage.setItem(TOKEN_KEY, legacy);
      localStorage.removeItem(TOKEN_KEY);
      return legacy;
    }
  }
  return null;
}

export function setAuthToken(token: string): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(TOKEN_KEY, token);
  if (canUseLocalStorage()) {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function clearAuthToken(): void {
  if (canUseSessionStorage()) {
    sessionStorage.removeItem(TOKEN_KEY);
  }
  if (canUseLocalStorage()) {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getAuthProvider(): string | null {
  if (canUseSessionStorage()) {
    const fromSession = sessionStorage.getItem(AUTH_PROVIDER_KEY);
    if (fromSession) return fromSession;
  }
  if (canUseLocalStorage()) {
    const legacy = localStorage.getItem(AUTH_PROVIDER_KEY);
    if (legacy && canUseSessionStorage()) {
      sessionStorage.setItem(AUTH_PROVIDER_KEY, legacy);
      localStorage.removeItem(AUTH_PROVIDER_KEY);
      return legacy;
    }
    return legacy;
  }
  return null;
}

export function setAuthProvider(provider: string): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(AUTH_PROVIDER_KEY, provider);
  if (canUseLocalStorage()) {
    localStorage.removeItem(AUTH_PROVIDER_KEY);
  }
}

export function clearAuthProvider(): void {
  if (canUseSessionStorage()) {
    sessionStorage.removeItem(AUTH_PROVIDER_KEY);
  }
  if (canUseLocalStorage()) {
    localStorage.removeItem(AUTH_PROVIDER_KEY);
  }
}

export function clearAuthSession(): void {
  clearAuthToken();
  clearAuthProvider();
}
